import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "./Area.css";
import { FeatureCollection } from "geojson";
import ct_bounds from "../../../data/boston_ct_2020.json";
import area_descriptions from "../../../data/HOLC_Area_Description_Boston_Data.json";
import Popup from "./Popup";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN as string;

type AreaMapProps = {
  selectionMap: MutableRefObject<Record<string, string>>;
};

const AreaMap: React.FC<AreaMapProps> = ({ selectionMap }) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const hoveredCT = useRef<string | undefined>(undefined);
  const clickedCT = useRef<string | undefined>(undefined);
  const selectedGeoID = useRef<string | undefined>(undefined);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupX, setPopupX] = useState(0);
  const [popupY, setPopupY] = useState(0);
  const [areaDescription, setAreaDescription] = useState("");

  // Initialize the map on mount
  useEffect(() => {
    // Creates the map object
    mapRef.current = new mapboxgl.Map({
      container: `area-map`,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [-71.057, 42.312],
      zoom: 11,
    });

    mapRef.current.on("load", () => {
      // Import the census tract geo data
      mapRef.current!.addSource("ct-bounds", {
        type: "geojson",
        data: ct_bounds as FeatureCollection,
        generateId: true,
      });

      // Create a layer for the fills of each census tract
      mapRef.current!.addLayer({
        id: "ct-fills",
        type: "fill",
        source: "ct-bounds",
        paint: {
          "fill-color": ["get", "fill"], // Normal fill color from properties
          "fill-opacity": [
            "case",
            ["boolean", ["feature-state", "clicked"], false],
            0.9, // Highest priority: clicked
            ["boolean", ["feature-state", "hover"], false],
            0.8, // Next priority: hover
            0.5, // Default when neither is true
          ],
        },
      });

      // Trace the borders of the census tracts
      mapRef.current!.addLayer({
        id: "ct-borders",
        type: "line",
        source: "ct-bounds",
        paint: {
          "line-color": "red",
          "line-width": 0.8,
          "line-opacity": 1,
        },
      });

      // EVENT HANDLERS
      // Triggers when the mouse moves within the polygon layer.
      mapRef.current!.on("mousemove", "ct-fills", (event) => {
        if (event.features && event.features[0]) {
          // Calculates the new hovered census tract ID
          const newHoveredCtId = event.features[0].id as string;
          // If it's different from what was hovered over before, update the map
          if (newHoveredCtId !== hoveredCT.current) {
            // Unhover the previous census tract (if it exists)
            if (hoveredCT.current !== undefined) {
              mapRef.current!.setFeatureState(
                { source: "ct-bounds", id: hoveredCT.current },
                { hover: false }
              );
            }
            // Hover the new one!
            mapRef.current!.setFeatureState(
              { source: "ct-bounds", id: newHoveredCtId },
              { hover: true }
            );
            hoveredCT.current = newHoveredCtId;
          }
        }
      });

      // Triggers when the mouse leaves the polygon layer.
      mapRef.current!.on("mouseleave", "ct-fills", () => {
        // Unhover the previous census tract (if it exists)
        if (hoveredCT.current !== undefined) {
          mapRef.current!.setFeatureState(
            { source: "ct-bounds", id: hoveredCT.current },
            { hover: false }
          );
          hoveredCT.current = undefined;
        }
      });

      // Triggers when a census tract is clicked.
      mapRef.current!.on("click", "ct-fills", (event) => {
        if (event.features && event.features[0]) {
          event.preventDefault();
          // Get the ID of the clicked census tract
          const clickedCtId = event.features[0].id as string;

          if (clickedCtId !== clickedCT.current) {
            // Unclick the previous census tract (if it exists)
            if (clickedCT.current !== undefined) {
              mapRef.current!.setFeatureState(
                { source: "ct-bounds", id: clickedCT.current },
                { clicked: false }
              );
            }
            // Click the new one!
            mapRef.current!.setFeatureState(
              { source: "ct-bounds", id: clickedCtId },
              { clicked: true }
            );
            clickedCT.current = clickedCtId;
          }

          const areaId = event.features[0].properties!.area_id;
          const description =
            area_descriptions.find((desc) => desc.area_id === areaId)
              ?.clarifying_remarks || "No description available";
          setAreaDescription(description); // Set the retrieved description into the state

          const rect = mapRef.current!.getContainer().getBoundingClientRect();
          setPopupX(event.point.x);
          setPopupY(event.point.y);
          setIsPopupVisible(true);
          selectedGeoID.current = event.features[0].properties!.GEOID as string;
        }
      });

      // Triggers when the map is clicked outside of the CT layer.
      mapRef.current!.on("click", (event) => {
        // Hides the popup if the user clicks the map outside of the layer
        if (event.defaultPrevented === false) {
          setIsPopupVisible(false);
          // Unclick the previous census tract (if it exists)
          if (clickedCT.current !== undefined) {
            mapRef.current!.setFeatureState(
              { source: "ct-bounds", id: clickedCT.current },
              { clicked: false }
            );
          }
        }
      });
    });

    // Disable scroll zoom by default.
    // Enable scroll zoom when clicking on the map, and disable it again
    // when clicking outside the map.
    mapRef.current.scrollZoom.disable();
    const handleClick = (event: MouseEvent) => {
      const mapContainer = document.getElementById("area-map");
      if (mapContainer && !mapContainer.contains(event.target as Node)) {
        mapRef.current?.scrollZoom.disable();
      } else {
        mapRef.current?.scrollZoom.enable();
      }
    };
    document.addEventListener("click", handleClick);

    // On cleanup, remove the map
    return () => mapRef.current!.remove();
  }, []);

  const onButtonClick = () => {
    // Clear the click and close the popup
    clickedCT.current = undefined;
    setIsPopupVisible(false);
  };

  return (
    <>
      <div id="area-map" />
      {isPopupVisible && (
        <Popup x={popupX} y={popupY} description={areaDescription} />
      )}
    </>
  );
};

export default AreaMap;
